import { useCallback, useEffect, useRef } from 'react'

const PAYMENT_REDIRECT_STATE_KEY = 'trafficFinePayment.externalRedirectState'

const PAYMENT_REDIRECT_STATE = {
  PENDING: 'pending',
  LEFT_PAGE: 'left-page'
}

function getRedirectState() {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window.sessionStorage.getItem(PAYMENT_REDIRECT_STATE_KEY)
  } catch {
    return null
  }
}

function setRedirectState(state) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(PAYMENT_REDIRECT_STATE_KEY, state)
  } catch {}
}

function clearRedirectState() {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.removeItem(PAYMENT_REDIRECT_STATE_KEY)
  } catch {}
}

function isPageVisible() {
  if (typeof document === 'undefined') {
    return true
  }

  return document.visibilityState === 'visible'
}

export default function useExternalPaymentRedirect(setIsSubmitting) {
  const removeLeaveListenersRef = useRef(() => {})

  const resetSubmittingAfterReturn = useCallback(() => {
    if (getRedirectState() !== PAYMENT_REDIRECT_STATE.LEFT_PAGE) {
      return
    }

    clearRedirectState()
    setIsSubmitting(false)
  }, [setIsSubmitting])

  const resetSubmittingWhenPageVisible = useCallback(() => {
    if (!isPageVisible()) {
      return
    }

    resetSubmittingAfterReturn()
  }, [resetSubmittingAfterReturn])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    resetSubmittingAfterReturn()
    window.addEventListener('pageshow', resetSubmittingAfterReturn)
    window.addEventListener('focus', resetSubmittingAfterReturn)

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', resetSubmittingWhenPageVisible)
    }

    return () => {
      window.removeEventListener('pageshow', resetSubmittingAfterReturn)
      window.removeEventListener('focus', resetSubmittingAfterReturn)

      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', resetSubmittingWhenPageVisible)
      }

      removeLeaveListenersRef.current()
    }
  }, [resetSubmittingAfterReturn, resetSubmittingWhenPageVisible])

  return useCallback(
    (url) => {
      if (typeof window === 'undefined' || !url) {
        return
      }

      removeLeaveListenersRef.current()
      setRedirectState(PAYMENT_REDIRECT_STATE.PENDING)

      let hasLeftPage = false

      const markLeftPageAndStopSubmitting = () => {
        if (hasLeftPage || getRedirectState() !== PAYMENT_REDIRECT_STATE.PENDING) {
          return
        }

        hasLeftPage = true
        setRedirectState(PAYMENT_REDIRECT_STATE.LEFT_PAGE)
        setIsSubmitting(false)
        removeLeaveListenersRef.current()
      }

      const handleVisibilityChange = () => {
        if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
          return
        }

        markLeftPageAndStopSubmitting()
      }

      const removeLeaveListeners = () => {
        window.removeEventListener('pagehide', markLeftPageAndStopSubmitting)

        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', handleVisibilityChange)
        }

        if (removeLeaveListenersRef.current === removeLeaveListeners) {
          removeLeaveListenersRef.current = () => {}
        }
      }

      removeLeaveListenersRef.current = removeLeaveListeners
      window.addEventListener('pagehide', markLeftPageAndStopSubmitting, { once: true })

      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange)
      }

      try {
        window.location.href = url
      } catch {
        removeLeaveListeners()
        clearRedirectState()
        setIsSubmitting(false)
      }
    },
    [setIsSubmitting]
  )
}
